import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { deriveDayInfo } from './sessionSelectors';

const countLabel = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;

// One clickable day card. Shows weekday + date and a compact session/horizon
// count, and highlights when it is the selected target day.
const DayCard = ({ info, selected, onSelect, cardRef }) => (
  <button
    ref={cardRef}
    type="button"
    onClick={() => onSelect(info.targetDay)}
    aria-pressed={selected}
    className={`flex w-24 sm:w-32 flex-shrink-0 flex-col gap-0.5 sm:gap-1 rounded-lg border p-2.5 sm:p-3 text-left transition-colors ${
      selected
        ? 'border-brand-300 dark:border-brand-700 bg-brand-50 dark:bg-brand-900/30'
        : 'border-gray-100 dark:border-gray-700 bg-surface dark:bg-gray-870 hover:bg-gray-50 dark:hover:bg-gray-700'
    }`}
  >
    <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
      {moment(info.targetDay).format('ddd')}
    </span>
    <span
      className={`text-sm font-bold ${
        selected ? 'text-brand-700 dark:text-brand-300' : 'text-gray-900 dark:text-gray-100'
      }`}
    >
      {moment(info.targetDay).format('MMM DD')}
    </span>
    <span className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
      {countLabel(info.sessionCount, 'session', 'sessions')}
    </span>
    <span className="text-[11px] text-gray-500 dark:text-gray-400">
      {countLabel(info.horizonLabels.length, 'horizon', 'horizons')}
    </span>
  </button>
);

// Minimal scroll affordance overlaid on a strip edge: a bare chevron over the
// edge fade, no chrome. Only rendered when that direction has more content;
// clicking nudges the strip ~one viewport.
const ScrollButton = ({ direction, onClick }) => {
  const Icon = direction === 'left' ? ChevronLeftIcon : ChevronRightIcon;
  return (
    <button
      type="button"
      aria-label={direction === 'left' ? 'Scroll to earlier days' : 'Scroll to later days'}
      onClick={onClick}
      className={`absolute top-1/2 z-20 -translate-y-1/2 p-0.5 text-gray-300 transition-colors hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-300 ${
        direction === 'left' ? 'left-0' : 'right-0'
      }`}
    >
      <Icon className="h-5 w-5" strokeWidth={2.5} />
    </button>
  );
};

// Horizontal, scrollable strip of recent target days (latest first) for quick
// scanning and one-click switching. Fed by the same session-history groups the
// page already loads — no extra fetch. The strip is dragged left/right (click
// and drag); edge fades + chevron buttons hint there is more. When a day is
// picked elsewhere (e.g. the date picker) and its card is off-screen, the strip
// scrolls just enough to reveal it; a visible card is left in place.
export const RecentDaysStrip = ({ dayGroups = [], selectedDate, onSelect }) => {
  const scrollerRef = useRef(null);
  const selectedRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  // Pointer drag-to-scroll with a light momentum glide on release. `moved`
  // guards the card click so a drag that ends on a card doesn't select that day.
  const drag = useRef({ down: false, startX: 0, startLeft: 0, moved: false, v: 0, lastX: 0, lastT: 0, raf: 0 });

  // Coast on the release velocity, easing out with friction.
  const glide = () => {
    const el = scrollerRef.current;
    if (!el) return;
    let v = drag.current.v;
    const tick = () => {
      el.scrollLeft += v;
      v *= 0.82;
      if (Math.abs(v) > 0.6) drag.current.raf = requestAnimationFrame(tick);
    };
    if (Math.abs(v) > 0.6) drag.current.raf = requestAnimationFrame(tick);
  };

  const onPointerDown = (event) => {
    const el = scrollerRef.current;
    if (!el) return;
    cancelAnimationFrame(drag.current.raf);
    const now = performance.now();
    drag.current = {
      down: true,
      startX: event.pageX,
      startLeft: el.scrollLeft,
      moved: false,
      v: 0,
      lastX: event.pageX,
      lastT: now,
      raf: 0,
    };
  };
  const onPointerMove = (event) => {
    const el = scrollerRef.current;
    if (!el || !drag.current.down) return;
    const dx = event.pageX - drag.current.startX;
    if (Math.abs(dx) > 3) drag.current.moved = true;
    el.scrollLeft = drag.current.startLeft - dx;
    // Track recent velocity (px per ~frame) to seed the glide.
    const now = performance.now();
    const dt = now - drag.current.lastT;
    if (dt > 0) drag.current.v = (-(event.pageX - drag.current.lastX) / dt) * 16;
    drag.current.lastX = event.pageX;
    drag.current.lastT = now;
  };
  const endDrag = () => {
    if (!drag.current.down) return;
    drag.current.down = false;
    glide();
  };
  const onClickCapture = (event) => {
    if (!drag.current.moved) return;
    event.preventDefault();
    event.stopPropagation();
    drag.current.moved = false;
  };

  // Stop any in-flight glide on unmount.
  useEffect(() => () => cancelAnimationFrame(drag.current.raf), []);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, dayGroups.length]);

  // Reveal the selected card when it sits outside the visible range — scrolling
  // the strip horizontally only (never the page) and just enough to bring it in.
  useEffect(() => {
    const el = scrollerRef.current;
    const card = selectedRef.current;
    if (!el || !card) return;
    const cardRect = card.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    if (cardRect.left < elRect.left) {
      el.scrollBy({ left: cardRect.left - elRect.left - 16, behavior: 'smooth' });
    } else if (cardRect.right > elRect.right) {
      el.scrollBy({ left: cardRect.right - elRect.right + 16, behavior: 'smooth' });
    }
  }, [selectedDate, dayGroups]);

  const scrollByViewport = (dir) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  if (dayGroups.length === 0) return null;

  return (
    <div className="mt-8">
      <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
        Recent days
      </div>
      <div className="relative">
        {canLeft && <ScrollButton direction="left" onClick={() => scrollByViewport(-1)} />}
        {canRight && <ScrollButton direction="right" onClick={() => scrollByViewport(1)} />}
        {/* Edge fades that hint at off-screen cards (don't block card clicks). */}
        {canLeft && (
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-14 bg-gradient-to-r from-surface via-surface/85 to-transparent dark:from-gray-870 dark:via-gray-870/85" />
        )}
        {canRight && (
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-14 bg-gradient-to-l from-surface via-surface/85 to-transparent dark:from-gray-870 dark:via-gray-870/85" />
        )}
        <div
          ref={scrollerRef}
          onMouseDown={onPointerDown}
          onMouseMove={onPointerMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onClickCapture={onClickCapture}
          className={`flex gap-3 overflow-x-auto pb-2 select-none ${
            canLeft || canRight ? 'cursor-grab active:cursor-grabbing' : ''
          }`}
        >
          {dayGroups.map((group) => {
            const info = deriveDayInfo(group);
            const selected = info.targetDay === selectedDate;
            return (
              <DayCard
                key={info.targetDay}
                info={info}
                selected={selected}
                onSelect={onSelect}
                cardRef={selected ? selectedRef : null}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
